import React from "react";
import { Button } from "primereact/button";
import { TenantListItem } from "../../../models/users";

interface TemplateProps {
  rowData: TenantListItem;
}

interface ActionTemplateProps extends TemplateProps {
  onEdit: (id: string) => void;
  onDelete: (tenant: TenantListItem) => void;
  // MT-013 — Navegar al CRUD de dominios del tenant (opcional).
  onDomains?: (id: string) => void;
}

interface ThemeTemplateProps extends TemplateProps {
  onThemeClick: (theme: string, provider: string) => void;
}

interface LogoTemplateProps extends TemplateProps {
  onLogoClick: (logo: string, provider: string) => void;
}

export const ActionBodyTemplate: React.FC<ActionTemplateProps> = ({
  rowData,
  onEdit,
  onDelete,
  onDomains,
}) => {
  return (
    <div className="flex gap-2">
      <Button
        icon="pi pi-pencil"
        size="small"
        className="p-button-rounded p-button-success"
        onClick={() => onEdit(rowData.id)}
        tooltip="Editar"
      />
      {onDomains && (
        <Button
          icon="pi pi-globe"
          size="small"
          className="p-button-rounded p-button-info"
          onClick={() => onDomains(rowData.id)}
          tooltip="Dominios"
        />
      )}
      <Button
        icon="pi pi-trash"
        size="small"
        className="p-button-rounded p-button-danger"
        onClick={() => onDelete(rowData)}
        tooltip="Eliminar"
      />
    </div>
  );
};

export const UserCountBodyTemplate: React.FC<TemplateProps> = ({ rowData }) => {
  return (
    <span className="flex align-items-center gap-2">
      <i className="pi pi-users"></i>
      {rowData.userCount}
    </span>
  );
};

export const ThemeBodyTemplate: React.FC<ThemeTemplateProps> = ({
  rowData,
  onThemeClick,
}) => {
  const theme = rowData.theme;
  const isJson = theme.startsWith('{') || theme.startsWith('[');
  
  const parseThemeColors = (themeString: string): string[] => {
    try {
      // Intentar parsear como JSON válido primero
      let themeObj: any;
      try {
        themeObj = JSON.parse(themeString);
      } catch {
        // Si falla, intentar corregir JSON comunes (propiedades sin comillas)
        const correctedJson = themeString
          .replace(/([{,]\s*)(\w+)(\s*:)/g, '$1"$2"$3') // agregar comillas a propiedades
          .replace(/:\s*'([^']+)'/g, ': "$1"'); // comillas simples a dobles
        themeObj = JSON.parse(correctedJson);
      }
      
      const colors: string[] = [];
      
      // Extraer colores principales del theme en orden de importancia
      const colorKeys = ['primary', 'primaryDark', 'primaryLight', 'secondary', 'accent', 'background'];
      colorKeys.forEach(key => {
        if (themeObj[key] && typeof themeObj[key] === 'string') {
          const color = themeObj[key];
          // Validar que sea un color válido
          if (
            color.startsWith('#') || 
            color.includes('rgb') || 
            color.match(/^#[0-9A-F]{3}$/i) || 
            color.match(/^#[0-9A-F]{6}$/i) || 
            color.match(/^(red|blue|green|yellow|orange|purple|pink|gray|black|white)$/i) ||
            color.match(/^#[0-9A-F]{8}$/i) // rgba en formato hex
          ) {
            colors.push(color);
          }
        }
      });
      
      return colors.slice(0, 4); // Mostrar máximo 4 colores
    } catch {
      return [];
    }
  };
  
  if (isJson) {
    const colors = parseThemeColors(theme);
    
    if (colors.length > 0) {
      return (
        <div 
          className="flex gap-2 align-items-center cursor-pointer hover:opacity-80 transition-all hover:scale-105 p-1"
          title="Click para ver colores del theme"
          onClick={() => onThemeClick(theme, rowData.provider)}
          style={{ borderRadius: '12px', border: '1px solid #f0f0f0', backgroundColor: '#fafafa' }}
        >
          {colors.map((color, index) => (
            <div
              key={index}
              className="border-circle"
              style={{
                width: '20px',
                height: '20px',
                backgroundColor: (color.startsWith('#') || color.includes('rgb') || /^#[0-9A-F]{6}$/i.test(color)) ? color : '#ccc',
                border: '1px solid #ddd',
                borderRadius: '50%',
                flexShrink: 0,
                boxShadow: color && (color.startsWith('#') || color.includes('rgb')) ? '0 2px 4px rgba(0,0,0,0.1)' : 'none'
              }}
              title={color}
            />
          ))}
          <i className="pi pi-palette text-gray-500 text-sm"></i>
        </div>
      );
    }
    
    // Si no hay colores válidos, mostrar indicador
    const hasValidJsonStructure = theme.startsWith('{') || theme.startsWith('[');
    
    return (
      <div 
        className="flex gap-2 align-items-center cursor-pointer hover:opacity-80 transition-all hover:scale-105 p-1"
        title="Click para ver detalles del theme"
        onClick={() => onThemeClick(theme, rowData.provider)}
        style={{ 
          borderRadius: '12px', 
          border: '1px solid #f0f0f0', 
          backgroundColor: '#fafafa',
          maxWidth: '200px',
          overflow: 'hidden'
        }}
      >
        {hasValidJsonStructure ? (
          // JSON sin colores válidos - mostrar texto del JSON truncado
          <>
            <div 
              className="border-circle"
              style={{
                width: '16px',
                height: '16px',
                backgroundColor: '#ffebee',
                border: '1px solid #ef5350',
                borderRadius: '50%',
                flexShrink: 0,
                backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(239,83,80,0.1) 3px, rgba(239,83,80,0.1) 6px)'
              }}
            />
            <span className="text-red-600 text-xs font-medium" style={{ whiteSpace: 'nowrap' }}>
              {theme.length > 15 ? `${theme.substring(0, 15)}...` : theme}
            </span>
            <i className="pi pi-palette text-red-400 text-xs"></i>
          </>
        ) : (
          // Formato completamente inválido
          <>
            <div 
              className="border-circle"
              style={{
                width: '16px',
                height: '16px',
                backgroundColor: '#e0e0e0',
                border: '1px solid #ccc',
                borderRadius: '50%',
                flexShrink: 0,
                backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(255,255,255,.5) 3px, rgba(255,255,255,.5) 6px)'
              }}
            />
            <span className="text-gray-500 text-xs font-medium" style={{ whiteSpace: 'nowrap' }}>
              {theme.length > 15 ? `${theme.substring(0, 15)}...` : theme}
            </span>
            <i className="pi pi-palette text-gray-400 text-xs"></i>
          </>
        )}
      </div>
    );
  }
  
  return <span className="text-sm">{theme}</span>;
};

export const TenantDateBodyTemplate: React.FC<TemplateProps> = ({ rowData }) => {
  if (!rowData.createdAt) {
    return <span className="text-gray-400">Sin fecha</span>;
  }
  
  try {
    const date = new Date(rowData.createdAt);
    if (isNaN(date.getTime())) {
      return <span className="text-gray-400">Fecha inválida</span>;
    }
    return (
      <span>
        {date.toLocaleDateString('es-ES', { 
          year: 'numeric', 
          month: '2-digit', 
          day: '2-digit' 
        })}
      </span>
    );
  } catch (error) {
    return <span className="text-gray-400">Error en fecha</span>;
  }
};

export const LogoBodyTemplate: React.FC<LogoTemplateProps> = ({
  rowData,
  onLogoClick,
}) => {
  if (rowData.logo) {
    return (
      <div className="flex align-items-center gap-2">
        <img 
          src={rowData.logo} 
          alt="Logo" 
          className="w-3rem h-2rem border-round cursor-pointer hover:opacity-80"
          style={{ objectFit: 'contain', transition: 'opacity 0.3s ease' }}
          onClick={() => onLogoClick(rowData.logo!, rowData.provider)}
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
            (e.target as HTMLImageElement).nextElementSibling!.textContent = 'Error al cargar';
          }}
          title="Click para ampliar"
        />
        <span style={{ display: 'none' }}></span>
      </div>
    );
  }
  return <span className="text-gray-400">Sin logo</span>;
}; 